// Copyright (c) Ensho Health, 2021 to present

import {
  oktaClient,
  handleMFAChallenge,
  handleMFARequired,
  handleMFAEnroll,
  handleMFAEnrollActivate
} from '../../okta.js';

import { LOCAL_STORAGE } from '../../constants.js';

const PREFIX = 'auth';
export const ACTION_TYPES = {
  LOGIN: `${PREFIX}-LOGIN`,
  LOGOUT: `${PREFIX}-LOGOUT`,
  ERROR: `${PREFIX}-ERROR`,
  SET_CLIENT: `${PREFIX}-SET_CLIENT`,
  SET_USER: `${PREFIX}-SET_USER`,
  UPDATE_AUTH_TRANSACTION: `${PREFIX}-UPDATE_AUTH_TRANSACTION`,
  LOADING: `${PREFIX}-LOADING`,
  STOP_LOADING: `${PREFIX}-STOP_LOADING`,
  IS_AUTHENTICATED: `${PREFIX}-IS_AUTHENTICATED`
};

const ERROR_MESSAGES = {
  GENERIC: 'Invalid email and / or password',
  GENERIC_TOKEN: 'Invalid token. Please contact an Ensho administrator.',
  UNKNOWN_TRANSACTION_STATUS: 'Something went wrong, please try again',
  LOCKED_OUT:
    'You have been locked out of your account. Please contact an Ensho administrator.',
  NO_RECOVERY_TOKEN: 'No recovery token provided',
  NO_EMAIL: 'No email provided',
  NO_ANSWER: 'No answer provided',
  INVALID_ANSWER: 'The answer is invalid',
  UNRECOVERABLE:
    'Unrecoverable error. Please contact your Ensho representative for assistance. (EN000011)',
  MISSING_QR_CODE:
    'Impossible to find your QR code. Please contact an Ensho administrator.'
};

export function handleNewTransaction() {
  return async (dispatch, getState) => {
    const currentTransaction = getState().auth.transaction;
    try {
      if (currentTransaction) {
        dispatch({ type: ACTION_TYPES.LOADING });
        if (currentTransaction.status === 'SUCCESS') {
          await oktaClient.token.getWithRedirect({
            sessionToken: currentTransaction.sessionToken,
            scopes: ['openid', 'offline_access']
          });
        } else {
          let newTransaction = null;
          switch (currentTransaction.status) {
            case 'MFA_REQUIRED':
              newTransaction = await handleMFARequired(currentTransaction);
              break;
            case 'MFA_CHALLENGE':
              newTransaction = await handleMFAChallenge(currentTransaction);
              break;
            case 'MFA_ENROLL':
              newTransaction = await handleMFAEnroll(currentTransaction);
              if (!newTransaction.factor?.activation?.qrcode?.href) {
                throw new Error(ERROR_MESSAGES.MISSING_QR_CODE);
              }
              break;
            case 'MFA_ENROLL_ACTIVATE':
              newTransaction = await handleMFAEnrollActivate(
                currentTransaction
              );
              break;

            // nothing to do, the user should enter their new password here
            case 'PASSWORD_RESET':
              dispatch({ type: ACTION_TYPES.STOP_LOADING });
              break;

            case 'RECOVERY':
              dispatch({ type: ACTION_TYPES.STOP_LOADING });
              break;

            // nothing to do here, okta just sent a reset password link
            case 'RECOVERY_CHALLENGE':
              dispatch({ type: ACTION_TYPES.STOP_LOADING });
              break;

            // not handled by the FE
            case 'PASSWORD_EXPIRED':
            case 'PASSWORD_WARN':
              currentTransaction.cancel();
              break;

            case 'LOCKED_OUT':
              throw new Error(ERROR_MESSAGES.LOCKED_OUT);

            default:
              throw new Error(ERROR_MESSAGES.UNKNOWN_TRANSACTION_STATUS);
          }
          if (newTransaction) {
            dispatch({
              type: ACTION_TYPES.UPDATE_AUTH_TRANSACTION,
              payload: newTransaction
            });
          }
        }
      }
    } catch (error) {
      if (currentTransaction) {
        currentTransaction.cancel();
      }
      console.error('auth.action.handleNewTransaction: ', error.message);
      // generic okta failure exception
      let message = error.message;
      if (error.errorCode === 'E0000004') {
        message = ERROR_MESSAGES.GENERIC;
      }
      dispatch({ type: ACTION_TYPES.ERROR, payload: { message } });
    }
  };
}

export function login(info) {
  return async (dispatch) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });
      if (info.email && info.password) {
        const transaction = await oktaClient.signInWithCredentials({
          username: info.email,
          password: info.password
        });
        dispatch({
          type: ACTION_TYPES.UPDATE_AUTH_TRANSACTION,
          payload: transaction
        });
      } else {
        throw new Error(ERROR_MESSAGES.GENERIC);
      }
    } catch (error) {
      console.error('auth.action.login: ', error.message || error);
      // generic okta failure exception
      let message = error.message;
      if (error.errorCode === 'E0000004') {
        message = ERROR_MESSAGES.GENERIC;
      }
      dispatch({ type: ACTION_TYPES.ERROR, payload: { message } });
    }
  };
}

export function loginWithToken(info) {
  return async (dispatch) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });
      if (info.token) {
        const transaction = await oktaClient.signInWithCredentials({
          token: info.token
        });
        dispatch({
          type: ACTION_TYPES.UPDATE_AUTH_TRANSACTION,
          payload: transaction
        });
      } else {
        throw new Error(ERROR_MESSAGES.GENERIC_TOKEN);
      }
    } catch (error) {
      console.error('auth.action.loginWithToken: ', error.message);
      // generic okta failure exception
      let message = error.message;
      if (error.errorCode === 'E0000004') {
        message = ERROR_MESSAGES.GENERIC_TOKEN;
      }
      dispatch({ type: ACTION_TYPES.ERROR, payload: { message } });
    }
  };
}

export function logout() {
  return async (dispatch) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });
      await oktaClient.signOut();
      // deleting the original uri stored by okta
      sessionStorage.removeItem(LOCAL_STORAGE.OKTA_REFERRER);
      dispatch({ type: ACTION_TYPES.LOGOUT });
    } catch (error) {
      console.error('auth.action.logout: ', error.message);
      dispatch({
        type: ACTION_TYPES.ERROR,
        payload: { message: error.message }
      });
    }
  };
}

export function forgotPassword(info) {
  return async (dispatch) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });
      if (info.email) {
        const transaction = await oktaClient.forgotPassword({
          username: info.email,
          factorType: 'EMAIL'
        });
        dispatch({
          type: ACTION_TYPES.UPDATE_AUTH_TRANSACTION,
          payload: transaction
        });
      } else {
        throw new Error(ERROR_MESSAGES.NO_EMAIL);
      }
    } catch (error) {
      console.error('auth.action.forgotPassword: ', error.message);
      // generic okta failure exception
      let message = error.message;
      if (error.errorCode === 'E0000004') {
        message = ERROR_MESSAGES.GENERIC;
      }
      dispatch({ type: ACTION_TYPES.ERROR, payload: { message } });
    }
  };
}

export function verifyRecoveryToken(info) {
  return async (dispatch) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });
      if (info.recoveryToken) {
        const transaction = await oktaClient.verifyRecoveryToken({
          recoveryToken: info.recoveryToken
        });
        dispatch({
          type: ACTION_TYPES.UPDATE_AUTH_TRANSACTION,
          payload: transaction
        });
      } else {
        throw new Error(ERROR_MESSAGES.NO_RECOVERY_TOKEN);
      }
    } catch (error) {
      console.error('auth.action.verifyRecoveryToken: ', error.message);
      // generic okta failure exception
      let message = error.message;
      if (error.errorCode === 'E0000004') {
        message = ERROR_MESSAGES.GENERIC_TOKEN;
      }
      dispatch({ type: ACTION_TYPES.ERROR, payload: { message } });
    }
  };
}

export function sendRecoveryAnswer(info) {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });
      if (info.answer) {
        const currentTransaction = getState().auth.transaction;
        if (currentTransaction) {
          const newTransaction = await currentTransaction.answer({
            answer: info.answer
          });
          dispatch({
            type: ACTION_TYPES.UPDATE_AUTH_TRANSACTION,
            payload: newTransaction
          });
        } else {
          throw new Error(ERROR_MESSAGES.UNRECOVERABLE);
        }
      } else {
        throw new Error(ERROR_MESSAGES.NO_ANSWER);
      }
    } catch (error) {
      console.error(
        'auth.action.sendRecoveryAnswer: ',
        error.errorCode,
        error.message
      );
      // generic okta failure exception
      let message = error.message;
      if (error.errorCode === 'E0000006') {
        message = ERROR_MESSAGES.INVALID_ANSWER;
      }
      dispatch({ type: ACTION_TYPES.ERROR, payload: { message } });
    }
  };
}

export function resetPassword(info) {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: ACTION_TYPES.LOADING });
      if (info.password) {
        const currentTransaction = getState().auth.transaction;
        if (currentTransaction) {
          const newTransaction = await currentTransaction.resetPassword({
            newPassword: info.password
          });
          dispatch({
            type: ACTION_TYPES.UPDATE_AUTH_TRANSACTION,
            payload: newTransaction
          });
        } else {
          throw new Error(ERROR_MESSAGES.UNRECOVERABLE);
        }
      } else {
        throw new Error(ERROR_MESSAGES.GENERIC);
      }
    } catch (error) {
      console.error(
        'auth.action.sendRecoveryAnswer: ',
        error.errorCode,
        error.message
      );
      // generic okta failure exception
      let message = error.message;
      if (error.errorCode === 'E0000006') {
        message = ERROR_MESSAGES.INVALID_ANSWER;
      }
      dispatch({ type: ACTION_TYPES.ERROR, payload: { message } });
    }
  };
}

export function setError(message) {
  return { type: ACTION_TYPES.ERROR, payload: { message } };
}
